import {
  getLocationMatches,
  getNearestStore,
  getNearestStoresList,
  getGeoLocation,
  setGeoLocation,
  locationDebugMode,
  STORE_LOCATIONS_STATUS_KEY,
  PRODUCT_LOCATION_STATUS_KEY,
} from '//www.99bikes.co.nz/cdn/shop/t/802/assets/99-geolocation.js?manual=20260309&v=8887924790071122161779846424';

/**
 * Custom element for managing location selection (store and delivery postcode) via a drawer interface.
 * Extends DrawerComponent.
 */
class LocationDrawer extends DrawerComponent {
  static DEBOUNCE_TIME = 300;

  constructor() {
    super();

    this.selectors = {
      postcodeList: '[data-postcode-list]',
      postcodeInputs: '.postcode-form__input-field',
      deliveryInput: '[data-delivery-input]',
      storeCardList: '[data-store-list]',
      storeCard: '[data-store-card-item]',
      changeStoreTrigger: '[data-change-store-trigger]',
      changePostcodeTrigger: '[data-change-postcode-trigger]',
      postcodeForm: '.postcode-form',
      suburbContainer: '[data-suburb-container]',
      postcodeGroup: '.postcode-form__group',
      postcodeGroupWrapper: '.postcode-form__input-wrapper',
      postcodeClearButton: '[data-postcode-clear-button]',
      locationTrigger: '[data-location-trigger]',
      locationModalTrigger: '[data-location-modal-trigger]',
      postcodeMatchItem: '[data-match]',
    };

    this.classes = {
      postcodeListItem: 'postcode-form__list-item',
      storeCard: 'store-card',
      storeCardSelected: 'store-card--selected',
      locationDrawerEditStore: 'location-drawer--edit-store',
      locationDrawerEditPostcode: 'location-drawer--edit-postcode',
      editForm: 'postcode-form--edit',
      locationDrawerEmpty: 'location-drawer--empty',
    };

    // --- Get the elements ---
    this.postcodeInputs = this.querySelectorAll(this.selectors.postcodeInputs);
    this.postcodeClearButtons = this.querySelectorAll(this.selectors.postcodeClearButton);
    this.storeCardList = this.querySelector(this.selectors.storeCardList);
    this.suburbContainer = this.querySelector(this.selectors.suburbContainer);
    this.changeStoreTrigger = this.querySelector(this.selectors.changeStoreTrigger);
    this.changePostcodeTrigger = this.querySelector(this.selectors.changePostcodeTrigger);
    this.locationTrigger = this.querySelectorAll(this.selectors.locationTrigger);
    this.deliveryInput = this.querySelector(this.selectors.deliveryInput);

    // --- Bind event handler methods ---
    this._handleEditForm = this._handleEditForm.bind(this);
    this._handlePostcodeInput = this._handlePostcodeInput.bind(this);
    this._handleClearPostcodeInput = this._handleClearPostcodeInput.bind(this);
    this._handleLocationTrigger = this._handleLocationTrigger.bind(this);
    this._handleGeolocationUpdate = this._handleGeolocationUpdate.bind(this);
    this._handlePostcodeMatchClick = this._handlePostcodeMatchClick.bind(this);
    this._handleStoreCardClick = this._handleStoreCardClick.bind(this);
    this._handleDeliveryStoreUpdate = this._handleDeliveryStoreUpdate.bind(this);
    this._handleOpenLocationDrawer = this._handleOpenLocationDrawer.bind(this);

    // 99B 20251215 -PO, open location draw event handled differently across site. For example:
    // Open directly to edit C&C or delivery location, or refresh based on product or cart availability.
    // Here, keep track of event listener functions added, to remove on disconnectedCallback
    this.handleOpenLocationDrawerFunctions = new Map();

    this.init();
  }

  /**
   * Lifecycle callback executed when the element is added to the DOM.
   * Adds event listeners.
   */
  connectedCallback() {
    super.connectedCallback();

    document.querySelectorAll(this.selectors.locationModalTrigger).forEach(trigger => {
      const handleOpenLocationDrawerFunction = () => { return this._handleOpenLocationDrawer(trigger) };
      trigger?.addEventListener('click', handleOpenLocationDrawerFunction);
      this.handleOpenLocationDrawerFunctions.set(trigger, handleOpenLocationDrawerFunction);
    });

    this.postcodeInputs.forEach(input => {
      input.addEventListener(
        'input',
        FoxTheme.utils.debounce(this._handlePostcodeInput, LocationDrawer.DEBOUNCE_TIME)
      );
    });

    this.postcodeClearButtons.forEach(button => {
      button.addEventListener('click', this._handleClearPostcodeInput);
    });

    this.locationTrigger.forEach(trigger => {
      trigger.addEventListener('click', this._handleLocationTrigger);
    });

    if (this.changeStoreTrigger) {
      this.changeStoreTrigger.addEventListener('click', this._handleEditForm);
    }

    if (this.changePostcodeTrigger) {
      this.changePostcodeTrigger.addEventListener('click', this._handleEditForm);
    }

    FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.geolocationUpdated,
      this._handleGeolocationUpdate
    );

    FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.deliveryStoreUpdated,
      this._handleDeliveryStoreUpdate
    );

    this.addEventListener('click', this._handlePostcodeMatchClick);

    this.storeCardList?.addEventListener('click', this._handleStoreCardClick);
  }

  /**
   * Lifecycle callback executed when the element is removed from the DOM.
   * Removes event listeners.
   */
  disconnectedCallback() {
    super.disconnectedCallback();

    document.querySelectorAll(this.selectors.locationModalTrigger).forEach(trigger => {
      const handleOpenLocationDrawerFunction = this.handleOpenLocationDrawerFunctions.get(trigger);
      if(handleOpenLocationDrawerFunction) trigger?.removeEventListener('click', handleOpenLocationDrawerFunction);
    });

    this.postcodeInputs.forEach(input => {
      input.removeEventListener(
        'input',
        FoxTheme.utils.debounce(this._handlePostcodeInput, LocationDrawer.DEBOUNCE_TIME)
      );
    });

    this.postcodeClearButtons.forEach(button => {
      button.removeEventListener('click', this._handleClearPostcodeInput);
    });

    this.locationTrigger?.forEach(trigger => {
      trigger.removeEventListener('click', this._handleLocationTrigger);
    });

    if (this.changeStoreTrigger) {
      this.changeStoreTrigger.removeEventListener('click', this._handleEditForm);
    }

    if (this.changePostcodeTrigger) {
      this.changePostcodeTrigger.removeEventListener('click', this._handleEditForm);
    }

    // Remove listeners from loops
    this.removeEventListener('click', this._handlePostcodeMatchClick);
    this.storeCardList?.removeEventListener('click', this._handleStoreCardClick);
  }

  /**
   * Initializes the component by attempting to get the user's location
   * and setting the nearest store and suburb display.
   * Marks the drawer as empty if no location is found initially.
   */
  async init() {
    let userLocation = null;

    if (!locationDebugMode()) {
      userLocation = await getGeoLocation();
    }

    if (userLocation) {
      this.setNearestStore({
        latitude: userLocation.lat,
        longitude: userLocation.long,
        useCache: true,
      });
      this.setSuburb();
    } else {
      this.classList.add(this.classes.locationDrawerEmpty);
      this.dataset.empty = true;
      // Clear the postcode lists
      this.querySelectorAll(this.selectors.postcodeList).forEach(list => {
        list.innerHTML = '';
      });
    }
  }

  /**
   * Shows the drawer and traps focus.
   * @param {HTMLElement | null} [focusElement=null] - Element to focus upon opening.
   * @param {boolean} [animate=true] - Whether to animate the opening.
   */
  show(focusElement = null, animate = true) {
    super.show(focusElement, animate);

    if (this.open && !Shopify.designMode) {
      FoxTheme.a11y.trapFocus(this, this.focusElement);
    }
  }

  /**
   * Fetches postcode matches and renders them in the specified list element.
   * Adds click listeners to the generated match elements.
   * @param {string} postcode - The postcode search term.
   * @param {HTMLElement} postcodeListElement - The element to display results in.
   */
  getPostcodeMatches = async (postcode, postcodeListElement) => {
    if (!postcodeListElement) return;
    const isDelivery = postcodeListElement.dataset.postcodeList == 'delivery';

    const matches = await getLocationMatches(postcode);
    postcodeListElement.innerHTML = '';

    matches.forEach(match => {
      const { locality, postcode: matchPostcode, state } = match;
      const stringifiedMatch = JSON.stringify(match);

      const el = document.createElement('div');
      el.classList.add(this.classes.postcodeListItem);
      el.setAttribute('data-match', stringifiedMatch);
      el.innerHTML = `${locality.toLowerCase()} ${state} <span>${matchPostcode}</span>`;

      postcodeListElement.appendChild(el);
    });
  };

  /**
   * Sets the displayed suburb text. Optionally uses provided location data.
   * @param {object|null} [existingLocationData=null] - Pre-fetched location data to use, if available.
   */
  setSuburb = async (existingLocationData = null) => {
    let locationData = existingLocationData;

    if (!locationData) {
      locationData = await getGeoLocation();
    }

    const { locality, state, postcode } = locationData;

    if (!this.suburbContainer || !locationData) return;
    this.suburbContainer.innerHTML = `${
      locality.charAt(0).toUpperCase() + locality.slice(1).toLowerCase()
    } ${state}, ${postcode}`;
  };

  /**
   * Finds the nearest store based on coordinates and updates the store display.
   * Optionally uses provided store data if a specific store is selected.
   * @param {object} geolocation - Holds the latitide longitude and if it should use the cache
   * @param {object|null} [updatedStore=null] - Pre-selected store data.
   */
  setNearestStore = async (geolocation, updatedStore = null) => {
    const store = await getNearestStore(geolocation, updatedStore);

    if (!store) return;

    if (this.storeCardList) {
      this.storeCardList.innerHTML = '';
      this.storeCardList.appendChild(this.createStoreCard(store, true));
    }
  };

  /**
   * Finds and displays a list of the nearest stores based on coordinates.
   * Adds click listeners to the generated store cards.
   * @param {Object} data - The geolocation data.
   * @param {HTMLElement} postcodeListElement - The list element containing postcode matches (to be cleared).
   */
  setNearestStoreList = async (data, postcodeListElement) => {
    const { long, lat } = data;

    if (postcodeListElement) {
      postcodeListElement.innerHTML = '';
    }

    if (this.storeCardList) {
      this.storeCardList.innerHTML = '';
    }

    const stores = await getNearestStoresList(lat, long);

    if (!stores || !stores.length || !this.storeCardList) return;

    stores.forEach((store, index) => {
      this.storeCardList.appendChild(this.createStoreCard(store, store.isSelected || false));
    });
  };

  /**
   * Creates a store card element (button) with store details.
   * Adds a click listener if it's not the currently selected store.
   * @param {object} store - The store data object.
   * @param {boolean} [isSelected=false] - Whether this card represents the currently selected primary store.
   * @returns {HTMLButtonElement} The created store card button element.
   */
  createStoreCard = (store, isSelected = false) => {
    const el = document.createElement('div');
    el.classList.add(this.classes.storeCard, isSelected && this.classes.storeCardSelected);
    el.setAttribute('data-store', JSON.stringify(store));

    const storeName = store?.name;
    const distanceKm = store?.distance ? `${store.distance}km` : ''; // Format distance
    const storeAddress = store?.address ?? '';

    const tradingHours = store?.todays_hours ?? '';
    let todaysHours = '';

    if (tradingHours) {
      try {
        const todaysHourJSON = JSON.parse(tradingHours);
        const todaysTradingTimes = todaysHourJSON.hours.time;

        if (todaysTradingTimes.includes('Closed')) {
          todaysHours = 'Closed Today';
        } else if (todaysTradingTimes.includes('Opening Soon')) {
          todaysHours = '<b>Opening Soon!</b>'
        } else if (todaysTradingTimes) {
          todaysHours = `<b>Open Today:</b> ${todaysTradingTimes}`;
        }
      } catch (error) {
        console.error('Error parsing todays_hours', error);
      }
    }

    // Get store status from cache - render on card if applicable
    let storeStatus, storeStatusText;
    let storeStatusTextOptions = [
      'In Stock',
      'Available nearby',
      'Available in x-y days',
      'Available in x-y days',
      'Unavailable',
    ];

    let storeLocationsStatuses = null;

    if (this.context === 'cart') {
      try {
        storeLocationsStatuses = JSON.parse(localStorage.getItem(STORE_LOCATIONS_STATUS_KEY));
      } catch (error) {
        storeLocationsStatuses = null;
        console.error('Error parsing store locations statuses:', error);
      }
    } else if (this.context === 'product') {
      try {
        storeLocationsStatuses = JSON.parse(localStorage.getItem(PRODUCT_LOCATION_STATUS_KEY));
      } catch (error) {
        storeLocationsStatuses = null;
        console.error('Error parsing store locations statuses:', error);
      }
    }

    if (storeLocationsStatuses && storeLocationsStatuses.length > 0) {
      const thisStoreStatus = storeLocationsStatuses.find(
        status => status.locationId === store.store_reference
      );

      if (thisStoreStatus) {
        storeStatus = thisStoreStatus.status;
        storeStatusText = storeStatusTextOptions[thisStoreStatus.status];
        if (thisStoreStatus.status === 3 && thisStoreStatus?.estimate) {
          storeStatusText = storeStatusText.replace('x', thisStoreStatus?.estimate[0]);
          storeStatusText = storeStatusText.replace('y', thisStoreStatus?.estimate[1]);
        }
        if (thisStoreStatus.status === 2 && thisStoreStatus?.comingSoonDate) {
          storeStatusText = `Coming soon: ${thisStoreStatus?.comingSoonDate}`;
        }
      }
    }

    el.innerHTML = `
      ${isSelected ? `<div class='store-card__badge'>Your Store</div>` : ''}
      <div class='store-card__header h5'>
        <div>${storeName}</div>
        ${distanceKm ? `<div>${distanceKm}</div>` : ''}
      </div>
      ${todaysHours ? `<div class='store-card__hours'>${todaysHours}</div>` : ''}
      <a target='_blank' href='https://www.google.com/maps/search/?api=1&query=${storeAddress}' class='store-card__name-address'>${storeAddress}</a>
      ${
        storeStatus !== undefined &&
        storeStatus !== null &&
        (this.context == 'product' || this.context == 'cart')
          ? `<div class='store-card__status stock-status' data-status="${storeStatus}">${storeStatusText}</div>`
          : ''
      }
      `;

    el.addEventListener('click', event => {
      this.setNearestStore(
        { latitude: store.latitude, longitude: store.longitude, useCache: false },
        store
      );

      const postcodeForm = event.target.closest(this.selectors.postcodeForm);
      if (postcodeForm) {
        postcodeForm.classList.remove(this.classes.editForm);
      }
    });

    return el;
  };

  /**
   * 99B 20251215 -PO, moved to own function to remove at end of lifetime (had memory leak)
   * Handles click to open location drawer
   * @param {object} trigger - The trigger button the call is made from
   * @private
   */
  _handleOpenLocationDrawer(trigger) {
    const context = trigger.dataset.modalContext;
    // Refresh the store cards & statuses depending on the context the modal was opened from
    if (context === 'cart' || context === 'product' || (!context && this.context)) {
      this.context = context;
      this.init();
    }

    this.show(); 

    // TODO: Refactor to nicer solution...
    // Toggle with change delivery address form open
    if (trigger.dataset.locationModalTrigger === 'changeDeliverTo') {
      this.querySelectorAll(this.selectors.postcodeForm)[1].classList.add(
        this.classes.editForm
      );
    }

    if (trigger.dataset.locationModalTrigger === 'selectPickUpStore') {
      this.querySelectorAll(this.selectors.postcodeForm)[0].classList.add(
        this.classes.editForm
      );
    }
  }

  /**
   * Handles clicks on the change store/postcode triggers, toggling the edit state.
   * @param {Event} event - The click event object.
   * @private
   */
  _handleEditForm(event) {
    const { target } = event;
    const postcodeForm = target.closest(this.selectors.postcodeForm);
    if (postcodeForm) {
      postcodeForm.classList.toggle(this.classes.editForm);
    }
  }

  /**
   * Handles input events on postcode fields, triggering postcode matching.
   * @param {Event} event - The input event object.
   * @private
   */
  _handlePostcodeInput(event) {
    const inputElement = event.target;
    const groupElement = inputElement.closest(this.selectors.postcodeGroup);
    const postcodeListElement = groupElement?.querySelector(this.selectors.postcodeList);

    if (postcodeListElement) {
      this.getPostcodeMatches(inputElement.value, postcodeListElement);
    } else {
      console.warn('Could not find associated postcode list for input:', inputElement);
    }
  }

  /**
   * Handles clicks on the clear button for postcode inputs.
   * @param {Event} event - The click event object.
   * @private
   */
  _handleClearPostcodeInput(event) {
    const groupElement = event.target.closest(this.selectors.postcodeGroup);
    const inputElement = groupElement?.querySelector(this.selectors.postcodeInputs);
    const postcodeListElement = groupElement?.querySelector(this.selectors.postcodeList);

    if (postcodeListElement) {
      postcodeListElement.innerHTML = '';
    }

    if (inputElement) {
      inputElement.value = '';
    }
  }

  /**
   * Handles clicks on the "Use My Location" trigger. Fetches geolocation
   * and updates either the store list or the delivery suburb based on context.
   * @param {Event} event - The click event object.
   * @private
   */
  async _handleLocationTrigger(event) {
    const trigger = event.target;
    const groupWrapper = trigger.closest(this.selectors.postcodeGroupWrapper);
    const groupElement = groupWrapper?.querySelector(this.selectors.postcodeGroup);
    const group = groupElement?.dataset.group;

    if (groupElement) {
      groupElement.classList.toggle(this.classes.editForm);
    }

    const useCache = false;
    const locationData = await getGeoLocation(useCache);

    if (this.dataset.empty == 'true') {
      if (!locationData) return;

      const nearestStore = await getNearestStore({
        latitude: locationData.lat,
        longitude: locationData.long,
        useCache: false,
      });

      if (nearestStore) {
        this.setNearestStore({
          latitude: nearestStore.latitude,
          longitude: nearestStore.longitude,
          useCache: false,
        });
        this.setSuburb();
      }

      this.dataset.empty = false;
      this.classList.remove(this.classes.locationDrawerEmpty);
    } else if (group == 'delivery') {
      if (!locationData) return;

      this.setSuburb(locationData);
      const postcodeForm = trigger.closest(this.selectors.postcodeForm);
      if (postcodeForm) {
        postcodeForm.classList.remove(this.classes.editForm);
      }
    } else if (group == 'store') {
      this.setNearestStoreList(locationData);
      const postcodeForm = trigger.closest(this.selectors.postcodeForm);
      if (postcodeForm) {
        postcodeForm.classList.remove(this.classes.editForm);
      }
    }
  }

  /**
   * Handles updates from the geolocation service (e.g., postcode change).
   * @param {object} data - The updated geolocation data.
   * @private
   */
  _handleGeolocationUpdate(data) {
    this.deliveryInput.value = data.postcode;
  }

  /**
   * Handles updates from the delivery store service (e.g., store change).
   * @param {object} store - The updated delivery store data.
   * @private
   */
  _handleDeliveryStoreUpdate(store) {
    if (this.storeCardList) {
      this.storeCardList.innerHTML = '';
      this.storeCardList.appendChild(this.createStoreCard(store, true));
    }
  }

  /**
   * Handles clicks within the component, specifically looking for postcode matches.
   * @param {Event} event - The click event object.
   * @private
   */
  _handlePostcodeMatchClick(event) {
    const matchElement = event.target.closest(this.selectors.postcodeMatchItem);

    if (!matchElement) {
      return;
    }

    const postcodeListElement = matchElement.closest(this.selectors.postcodeList);
    if (!postcodeListElement) return;

    const isDelivery = postcodeListElement.dataset.postcodeList === 'delivery';
    const matchDataString = matchElement.dataset.match;

    if (!matchDataString) return;

    try {
      const deliveryLocation = JSON.parse(matchDataString);

      if (!isDelivery) {
        this.setNearestStoreList(deliveryLocation, postcodeListElement);
      } else {
        setGeoLocation(deliveryLocation);
        this.setSuburb();
        event.target.closest(this.selectors.postcodeForm)?.classList.remove(this.classes.editForm);

        if (this.dataset.empty === 'true') {
          this.dataset.empty = 'false';
          this.classList.remove(this.classes.locationDrawerEmpty);
          this.setNearestStore({
            latitude: deliveryLocation.lat,
            longitude: deliveryLocation.long,
            useCache: false,
          });
        }
        postcodeListElement.innerHTML = '';
      }
    } catch (error) {
      console.error('Error parsing postcode match data:', error);
    }
  }

  /**
   * Handles clicks within the store card list.
   * @param {Event} event - The click event object.
   * @private
   */
  _handleStoreCardClick(event) {
    const cardElement = event.target.closest(this.selectors.storeCard);

    if (!cardElement) {
      return;
    }

    const storeDataString = cardElement.dataset.store;
    if (!storeDataString) return;

    try {
      const store = JSON.parse(storeDataString);

      this.setNearestStore(
        { latitude: store.latitude, longitude: store.longitude, useCache: false },
        store
      );
      event.target.closest(this.selectors.postcodeForm)?.classList.remove(this.classes.editForm);
    } catch (error) {
      console.error('Error parsing store card data:', error);
    }
  }
}
customElements.define('location-drawer', LocationDrawer);
